import { Badge } from "@frontend/components/ui/badge"
import { Button } from "@frontend/components/ui/button"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@frontend/components/ui/resizable"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import { extractValidationErrors } from "@frontend/lib/api-error"
import { AnnotationTimeStatusBar } from "@frontend/pages/case-workspace/components/annotation-time-status-bar"
import { ClinicalHistoryPanel } from "@frontend/pages/case-workspace/components/clinical-history-panel"
import { HistoryTimeline } from "@frontend/pages/case-workspace/components/history-timeline"
import { ReviewActionBar } from "@frontend/pages/case-workspace/components/review-action-bar"
import { useAnnotationHistoryBridge } from "@frontend/pages/case-workspace/hooks/use-annotation-history-bridge"
import { useModuleCompletionWatcher } from "@frontend/pages/case-workspace/hooks/use-module-completion-watcher"
import { isAmbientModuleType } from "@frontend/pages/case-workspace/lib/ambient-modules"
import {
  getModuleCompletionMap,
  getModuleDependencyLockMap
} from "@frontend/pages/case-workspace/lib/module-dependency-lock"
import { sortModulesByDependencyOrder } from "@frontend/pages/case-workspace/lib/module-dependency-order"
import { deriveEarliestStudyDate } from "@frontend/pages/case-workspace/lib/study-date"
import { validateModuleCompletion } from "@frontend/pages/case-workspace/lib/validate-module-completion"
import { setDraggedSeriesId } from "@frontend/pages/case-workspace/lib/viewer-dnd"
import {
  LeftSeriesPanel,
  PullTab,
  RightModulePanel
} from "@frontend/pages/case-workspace/radiology/components/module-panels"
import { useRadiologySession } from "@frontend/pages/case-workspace/radiology/hooks/use-radiology-session"
import { useSeedMaskLoader } from "@frontend/pages/case-workspace/radiology/hooks/use-seed-mask-loader"
import { useViewerVolumes } from "@frontend/pages/case-workspace/radiology/hooks/use-viewer-volumes"
import { buildMaskDrawingConfig } from "@frontend/pages/case-workspace/radiology/lib/mask-drawing-config"
import {
  describeMaskFilterMismatch,
  flattenMaskLabels,
  seriesMatchesMaskFilter
} from "@frontend/pages/case-workspace/radiology/lib/mask-utils"
import { parseModuleSchema } from "@frontend/pages/case-workspace/radiology/lib/module-schema"
import { NvScenePanel } from "@frontend/pages/case-workspace/radiology/viewer/panel"
import type { SamState } from "@frontend/pages/case-workspace/radiology/viewer/types"
import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { useHistoryStore } from "@frontend/stores/history-store"
import { NVImage } from "@niivue/niivue"
import { decodeRLE } from "@niivue/niivue/drawing"
import type { MaskNiftiEntry } from "@shared/lib/history-types"
import { useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, Pencil, Redo2, Save, Send, Undo2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePanelRef } from "react-resizable-panels"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"

import type { CaseDetails, CaseSeries, GeometryType, ProjectDetails, ProjectModulesResponse } from "./types"

interface AnnotationViewerPageProps {
  reviewSubmissionId?: string | null
}

export function AnnotationViewerPage({ reviewSubmissionId = null }: AnnotationViewerPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const api = useApiClient()
  const { projectId, caseId } = useParams<{
    projectId: string
    caseId: string
  }>()

  const [selectedSeriesId, setSelectedSeriesId] = useState<string | undefined>()
  const [activeViewerSeriesId, setActiveViewerSeriesId] = useState<string | null>(null)
  const [isSeriesDragging, setIsSeriesDragging] = useState(false)
  const [brushSize, setBrushSize] = useState([2])
  const [filledPen, setFilledPen] = useState(true)
  const [eraseMode, setEraseMode] = useState(false)
  const [samMode, setSamMode] = useState(false)
  const [samScribbleMode, setSamScribbleMode] = useState(false)
  const [drawingDisabled, setDrawingDisabled] = useState(false)
  const [samConfidence, setSamConfidence] = useState(0.5)
  const [samState, setSamState] = useState<SamState>({ samAvailable: false, samPending: false, samError: null })
  const [maskOpacityById, setMaskOpacityById] = useState<Record<string, number>>({})
  const [maskLastVisibleOpacity, setMaskLastVisibleOpacity] = useState<Record<string, number>>({})
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  const [activeVectorLabelKey, setActiveVectorLabelKey] = useState<string | null>(null)
  const [activeMaskInstanceId, setActiveMaskInstanceId] = useState<string | null>(null)
  const [activeGeometryType, setActiveGeometryType] = useState<GeometryType>("polygon")
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [moduleCompletionError, setModuleCompletionError] = useState<{
    moduleId: string
    errors: string[]
  } | null>(null)
  const leftPanelRef = usePanelRef()
  const rightPanelRef = usePanelRef()

  const hasHistorySession = useHistoryStore((s) => s.session !== null)
  const historyCursor = useHistoryStore((s) => s.session?.cursor ?? 0)
  const historyCheckpointCount = useHistoryStore((s) => s.session?.checkpoints.length ?? 0)
  const masks = useAnnotationStore((s) => s.masks)
  const scores = useAnnotationStore((s) => s.scores)
  const imageLabels = useAnnotationStore((s) => s.imageLabels)
  const texts = useAnnotationStore((s) => s.texts)
  const moduleCompletions = useAnnotationStore((s) => s.moduleCompletions)
  const upsertMask = useAnnotationStore((s) => s.upsertMask)
  const removeMask = useAnnotationStore((s) => s.removeMask)
  const upsertScore = useAnnotationStore((s) => s.upsertScore)
  const upsertImageLabel = useAnnotationStore((s) => s.upsertImageLabel)
  const upsertText = useAnnotationStore((s) => s.upsertText)
  const setModuleCompletion = useAnnotationStore((s) => s.setModuleCompletion)

  const { data: currentUser } = useApiQuery<{ instance_role: "admin" | "member" | null }>(["auth", "me"], "/auth/me")

  const { data: caseData } = useApiQuery<CaseDetails>(["cases", caseId!, "detail"], `/cases/${caseId}`, {
    enabled: Boolean(caseId)
  })
  const { data: projectData } = useApiQuery<ProjectDetails>(
    ["projects", projectId!, "detail"],
    `/projects/${projectId}`,
    { enabled: Boolean(projectId) }
  )
  const { data: projectModulesData } = useApiQuery<ProjectModulesResponse>(
    ["projects", projectId!, "modules"],
    `/projects/${projectId}/modules`,
    { enabled: Boolean(projectId) }
  )
  const { series } = useViewerVolumes(caseId ?? "", undefined, {
    loadFiles: false
  })

  // NIfTI generation callback for save-time mask export.
  // Uses a ref for auth headers to break the dependency cycle with useRadiologySession.
  const getAuthHeadersRef = useRef<() => Promise<Record<string, string> | undefined>>(undefined)
  const generateMaskNiftis = useCallback(async (): Promise<MaskNiftiEntry[]> => {
    const currentMasks = useAnnotationStore.getState().masks
    if (currentMasks.length === 0) return []

    // Group masks by the series they were actually drawn on, so each mask's
    // RLE is decoded against the geometry it was authored against. Using a
    // single shared reference series would truncate/zero-fill masks whose
    // series geometry differs (see issue #52).
    const masksBySeries = new Map<string, typeof currentMasks>()
    for (const mask of currentMasks) {
      if (!mask.rle || mask.rle.length === 0 || !mask.seriesId) continue
      const group = masksBySeries.get(mask.seriesId)
      if (group) group.push(mask)
      else masksBySeries.set(mask.seriesId, [mask])
    }
    if (masksBySeries.size === 0) return []

    // Bail out (once) if the viewer's series list hasn't loaded yet: without it
    // every group would fail its series lookup and spam warnings on each autosave.
    if (series.length === 0) {
      console.warn("generateMaskNiftis: series list not yet populated; skipping NIfTI generation")
      return []
    }

    const headers = await getAuthHeadersRef.current?.()
    const entries: MaskNiftiEntry[] = []

    for (const [seriesId, masks] of masksBySeries) {
      const refSeries = series.find((s) => s.id === seriesId)
      if (!refSeries) {
        console.warn(`generateMaskNiftis: no series found for seriesId ${seriesId}; skipping ${masks.length} mask(s)`)
        continue
      }

      // Catch per-series so one bad series/mask doesn't abort generation for the
      // rest (the caller swallows thrown errors as best-effort).
      try {
        const res = await fetch(`/api/cases/${caseId}/series/${refSeries.id}/file`, { headers })
        if (!res.ok) {
          console.warn(`generateMaskNiftis: failed to fetch series ${refSeries.id} file (${res.status})`)
          continue
        }

        const blobUrl = URL.createObjectURL(await res.blob())
        try {
          const refImage = await NVImage.loadFromUrl({ url: blobUrl, name: "ref.nii.gz" })
          const voxelCount = refImage.img?.length ?? 0
          if (voxelCount === 0) {
            console.warn(`generateMaskNiftis: reference series ${refSeries.id} has zero voxels`)
            continue
          }

          for (const mask of masks) {
            try {
              const bitmap = decodeRLE(mask.rle, voxelCount)
              const nifti = await refImage.saveToUint8Array("mask.nii.gz", bitmap)
              entries.push({ annotationId: mask.id, nifti })
            } catch (err) {
              console.warn(`generateMaskNiftis: failed to encode mask ${mask.id}`, err)
            }
          }
        } finally {
          URL.revokeObjectURL(blobUrl)
        }
      } catch (err) {
        console.warn(`generateMaskNiftis: failed to process series ${refSeries.id}`, err)
      }
    }

    return entries
  }, [caseId, series])

  const canViewTimeline =
    currentUser?.instance_role === "admin" ||
    projectData?.current_user_role === "instance_admin" ||
    projectData?.current_user_role === "project_admin" ||
    projectData?.current_user_role === "reviewer"

  const activeSeriesId = useMemo(() => selectedSeriesId ?? series[0]?.id, [selectedSeriesId, series])
  const studyDate = useMemo(() => deriveEarliestStudyDate(series as CaseSeries[]), [series])
  const activeMaskSeriesId = activeViewerSeriesId ?? activeSeriesId ?? null
  const projectModules = useMemo(
    () => sortModulesByDependencyOrder(projectModulesData?.data ?? []),
    [projectModulesData]
  )
  const interactiveModules = useMemo(
    () => projectModules.filter((m) => !isAmbientModuleType(m.template_type)),
    [projectModules]
  )
  const hasAnnotationTimeModule = projectModules.some((m) => m.template_type === "annotation_time")
  const moduleCompletionMap = useMemo(() => getModuleCompletionMap(moduleCompletions), [moduleCompletions])
  const moduleLockById = useMemo(
    () => getModuleDependencyLockMap(projectModules, moduleCompletionMap),
    [moduleCompletionMap, projectModules]
  )
  const activeModule = useMemo(
    () => interactiveModules.find((m) => m.id === activeModuleId) ?? interactiveModules[0] ?? null,
    [activeModuleId, interactiveModules]
  )
  const activeModuleLock = useMemo(
    () => (activeModule ? moduleLockById.get(activeModule.id) : null),
    [activeModule, moduleLockById]
  )
  const baseModuleEditable = activeModuleLock?.isEditable ?? true
  const activeModuleLockReason = activeModuleLock?.lockReason ?? null

  const isActiveModuleComplete = useMemo(
    () => moduleCompletions.find((c) => c.projectModuleId === activeModule?.id)?.completed ?? false,
    [activeModule?.id, moduleCompletions]
  )

  const {
    submissionId,
    pendingRestoreSnapshot,
    clearPendingRestoreSnapshot,
    applySnapshot,
    handleTimelineScrub,
    uploadHistoryNow,
    isHistoryDirty,
    isUploadingHistory,
    checkpointCount,
    isBootstrappingSession,
    markActiveModuleComplete,
    setModuleCompletionRemote,
    isReviewMode,
    reviewEditEnabled,
    reviewSubmission,
    reviewStatus,
    setReviewStatus,
    setReviewEditEnabled,
    refetchReviewSubmission,
    isViewOnlyMode,
    startAnnotating,
    isFreshSubmission,
    clearFreshSubmission,
    getAuthHeaders
  } = useRadiologySession({
    api,
    caseId,
    projectId,
    activeModuleId: activeModule?.id ?? null,
    isActiveModuleComplete,
    setModuleCompletion,
    reviewSubmissionId,
    currentUserRole: projectData?.current_user_role,
    generateMaskNiftis
  })
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders
  }, [getAuthHeaders])

  // Seed mask loading for fresh submissions with SEG series
  const { loadSeedMasks } = useSeedMaskLoader()
  const maskModule = useMemo(() => projectModules.find((m) => m.template_type === "mask") ?? null, [projectModules])

  useEffect(() => {
    if (!(isFreshSubmission && caseId && maskModule)) return
    if (!series.some((s) => s.image_type === "SEG")) {
      clearFreshSubmission()
      return
    }

    const maskSchema = parseModuleSchema(maskModule.template_schema)
    if (maskSchema?.type !== "mask") {
      clearFreshSubmission()
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const result = await loadSeedMasks({
          caseId,
          series,
          maskModuleId: maskModule.id,
          maskLabels: flattenMaskLabels(maskSchema),
          getAuthHeaders
        })
        if (!cancelled && result.masks.length > 0) {
          const store = useAnnotationStore.getState()
          for (const mask of result.masks) {
            store.upsertMask(mask)
          }
          const snapshot = store.getSnapshot()
          const history = useHistoryStore.getState()
          history.captureCheckpoint(snapshot, "mutation")
          if (result.niftis.length > 0) {
            history.setMaskNiftis(result.niftis)
          }
          applySnapshot(snapshot)
        }
      } finally {
        if (!cancelled) clearFreshSubmission()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    isFreshSubmission,
    caseId,
    maskModule,
    series,
    loadSeedMasks,
    clearFreshSubmission,
    getAuthHeaders,
    applySnapshot
  ])

  const isActiveModuleEditable = baseModuleEditable && (!isReviewMode || reviewEditEnabled) && !isViewOnlyMode

  const handleSelectModule = useCallback(
    (nextModuleId: string) => {
      if (nextModuleId === activeModuleId) return
      setActiveModuleId(nextModuleId)
      setActiveVectorLabelKey(null)
      setActiveMaskInstanceId(null)
      setActiveGeometryType("polygon")
      setSamMode(false)
      setSamScribbleMode(false)
    },
    [activeModuleId]
  )

  const handleMarkActiveModuleComplete = useCallback(async () => {
    if (!isActiveModuleEditable) return
    if (!activeModule) return

    // Only validate on the complete transition; un-completing bypasses checks.
    if (!isActiveModuleComplete) {
      const check = validateModuleCompletion({
        module: activeModule,
        modules: interactiveModules,
        masks,
        scores
      })
      if (!check.valid) {
        setModuleCompletionError({ moduleId: activeModule.id, errors: check.errors })
        toast.error(`Cannot mark ${activeModule.template_name} complete`, {
          description: check.errors.join("\n")
        })
        return
      }
    }

    try {
      await markActiveModuleComplete()
      setModuleCompletionError(null)
    } catch (err) {
      const errors = extractValidationErrors(err) ?? ["Failed to update completion"]
      setModuleCompletionError({ moduleId: activeModule.id, errors })
      toast.error(`Cannot mark ${activeModule.template_name} complete`, {
        description: errors.join("\n")
      })
      return
    }

    if (!isActiveModuleComplete) {
      const currentIdx = interactiveModules.findIndex((m) => m.id === activeModule.id)
      const next = interactiveModules.find(
        (m, i) => i > currentIdx && !moduleCompletions.find((c) => c.projectModuleId === m.id)?.completed
      )
      if (next) handleSelectModule(next.id)
    }
  }, [
    isActiveModuleEditable,
    activeModule,
    interactiveModules,
    masks,
    scores,
    markActiveModuleComplete,
    isActiveModuleComplete,
    moduleCompletions,
    handleSelectModule
  ])

  const activeMaskSchema = useMemo(() => {
    if (activeModule?.template_type !== "mask") return null
    const schema = parseModuleSchema(activeModule.template_schema)
    return schema?.type === "mask" ? schema : null
  }, [activeModule])

  const activeMaskSeriesInfo = useMemo(
    () => (series as CaseSeries[]).find((s) => s.id === activeMaskSeriesId) ?? null,
    [series, activeMaskSeriesId]
  )
  const maskSeriesEligible = useMemo(
    () => seriesMatchesMaskFilter(activeMaskSeriesInfo, activeMaskSchema?.series_filter),
    [activeMaskSeriesInfo, activeMaskSchema?.series_filter]
  )
  const maskFilterMismatchReason = useMemo(
    () => (!maskSeriesEligible ? describeMaskFilterMismatch(activeMaskSchema?.series_filter) : null),
    [maskSeriesEligible, activeMaskSchema?.series_filter]
  )

  const activeModuleMasks = useMemo(
    () => masks.filter((mask) => mask.projectModuleId === activeModule?.id && mask.seriesId === activeMaskSeriesId),
    [activeMaskSeriesId, activeModule?.id, masks]
  )

  const activeMaskLabelKey = useMemo(
    () => masks.find((m) => m.id === activeMaskInstanceId)?.labelName ?? null,
    [activeMaskInstanceId, masks]
  )

  useEffect(() => {
    if (!activeMaskInstanceId) return
    const hasActive = activeModuleMasks.some((mask) => mask.id === activeMaskInstanceId)
    if (!hasActive) {
      setActiveMaskInstanceId(null)
    }
  }, [activeMaskInstanceId, activeModuleMasks])

  const createMaskInstance = useCallback(
    (labelKey: string) => {
      if (!isActiveModuleEditable) return
      if (!maskSeriesEligible) return
      if (!activeModule || activeModule.template_type !== "mask") return
      if (!(activeMaskSeriesId && activeMaskSchema)) return
      const labels = flattenMaskLabels(activeMaskSchema)
      const labelIdx = labels.findIndex((label) => label.key === labelKey)
      if (labelIdx < 0) return
      const labelDef = labels[labelIdx]!
      const existing = masks
        .filter(
          (mask) =>
            mask.projectModuleId === activeModule.id &&
            mask.seriesId === activeMaskSeriesId &&
            mask.labelName === labelKey
        )
        .sort((a, b) => a.instanceOrdinal - b.instanceOrdinal)

      if (labelDef.instance_policy.mode === "single" && existing.length > 0) {
        setActiveMaskInstanceId(existing[0]!.id)
        return
      }

      const maxInstances = labelDef.instance_policy.max_instances
      if (labelDef.instance_policy.mode === "multiple" && maxInstances != null && existing.length >= maxInstances) {
        return
      }

      const nextOrdinal = existing.length > 0 ? Math.max(...existing.map((mask) => mask.instanceOrdinal)) + 1 : 1
      const nextId = crypto.randomUUID()
      upsertMask({
        id: nextId,
        seriesId: activeMaskSeriesId,
        projectModuleId: activeModule.id,
        labelName: labelKey,
        labelValue: labelDef.value ?? labelIdx + 1,
        instanceOrdinal: nextOrdinal,
        rle: new Uint8Array()
      })
      setActiveMaskInstanceId(nextId)
    },
    [activeMaskSchema, activeMaskSeriesId, activeModule, isActiveModuleEditable, maskSeriesEligible, masks, upsertMask]
  )

  const activateSingleMaskLabel = useCallback(
    (labelKey: string) => {
      if (!maskSeriesEligible) return
      const existing = activeModuleMasks
        .filter((mask) => mask.labelName === labelKey)
        .sort((a, b) => a.instanceOrdinal - b.instanceOrdinal)
      if (existing.length > 0) {
        setActiveMaskInstanceId(existing[0]!.id)
        return
      }
      createMaskInstance(labelKey)
    },
    [activeModuleMasks, createMaskInstance, maskSeriesEligible]
  )

  const deleteMaskInstance = useCallback(
    (annotationId: string) => {
      if (!isActiveModuleEditable) return
      removeMask(annotationId)
      if (activeMaskInstanceId === annotationId) {
        setActiveMaskInstanceId(null)
      }
    },
    [activeMaskInstanceId, isActiveModuleEditable, removeMask]
  )

  const handleMaskOpacityChange = useCallback((maskId: string, percent: number) => {
    setMaskOpacityById((prev) => ({ ...prev, [maskId]: percent }))
  }, [])

  const handleMaskOpacityCommit = useCallback((maskId: string, percent: number) => {
    setMaskOpacityById((prev) => ({ ...prev, [maskId]: percent }))
    if (percent > 0) {
      setMaskLastVisibleOpacity((prev) => ({ ...prev, [maskId]: percent }))
    }
  }, [])

  const handleMaskVisibilityToggle = useCallback(
    (maskId: string) => {
      const current = maskOpacityById[maskId] ?? 45
      if (current > 0) {
        setMaskLastVisibleOpacity((prev) => ({ ...prev, [maskId]: current }))
        setMaskOpacityById((prev) => ({ ...prev, [maskId]: 0 }))
        return
      }
      const restore = maskLastVisibleOpacity[maskId] ?? 45
      setMaskOpacityById((prev) => ({ ...prev, [maskId]: restore }))
    },
    [maskOpacityById, maskLastVisibleOpacity]
  )

  const maskDrawingConfig = useMemo(
    () =>
      buildMaskDrawingConfig({
        activeModuleId: activeModule?.id ?? null,
        activeMaskSchema,
        isActiveModuleEditable,
        maskSeriesEligible,
        activeMaskInstanceId,
        brushSize: brushSize[0] ?? 2,
        filledPen,
        eraseMode,
        samMode,
        samScribbleMode,
        samConfidence,
        drawingDisabled
      }),
    [
      activeMaskInstanceId,
      activeMaskSchema,
      activeModule?.id,
      brushSize,
      filledPen,
      eraseMode,
      samMode,
      samScribbleMode,
      samConfidence,
      drawingDisabled,
      isActiveModuleEditable,
      maskSeriesEligible
    ]
  )

  const handleBitmapCommit = useCallback(
    (params: { annotationId: string; seriesId: string | null; labelKey: string; rle: Uint8Array }) => {
      if (!isActiveModuleEditable) return
      if (!activeModule || activeModule.template_type !== "mask") return
      if (!activeMaskSchema) return
      const labels = flattenMaskLabels(activeMaskSchema)
      const labelIdx = labels.findIndex((l) => l.key === params.labelKey)
      if (labelIdx === -1) return
      const labelDef = labels[labelIdx]!

      const existing = masks.find((mask) => mask.id === params.annotationId)
      upsertMask({
        id: params.annotationId,
        seriesId: params.seriesId,
        projectModuleId: activeModule.id,
        labelName: params.labelKey,
        labelValue: labelDef.value ?? labelIdx + 1,
        instanceOrdinal: existing?.instanceOrdinal ?? 1,
        rle: params.rle
      })
    },
    [activeMaskSchema, activeModule, isActiveModuleEditable, masks, upsertMask]
  )

  const handleUpsertScore = useCallback(
    (...args: Parameters<typeof upsertScore>) => {
      if (!isActiveModuleEditable) return
      upsertScore(...args)
    },
    [isActiveModuleEditable, upsertScore]
  )

  const handleUpsertImageLabel = useCallback(
    (...args: Parameters<typeof upsertImageLabel>) => {
      if (!isActiveModuleEditable) return
      upsertImageLabel(...args)
    },
    [isActiveModuleEditable, upsertImageLabel]
  )

  const handleUpsertText = useCallback(
    (...args: Parameters<typeof upsertText>) => {
      if (!isActiveModuleEditable) return
      upsertText(...args)
    },
    [isActiveModuleEditable, upsertText]
  )

  const handleActiveVectorLabelKey = useCallback(
    (nextLabel: string | null) => {
      if (!isActiveModuleEditable) return
      setActiveVectorLabelKey(nextLabel)
    },
    [isActiveModuleEditable]
  )

  const handleActiveGeometryType = useCallback(
    (nextType: GeometryType) => {
      if (!isActiveModuleEditable) return
      setActiveGeometryType(nextType)
    },
    [isActiveModuleEditable]
  )

  const handleSamMode = useCallback((active: boolean) => {
    setSamMode(active)
    if (active) {
      setEraseMode(false)
      setSamScribbleMode(false)
    }
  }, [])

  const handleSamScribbleMode = useCallback((active: boolean) => {
    setSamScribbleMode(active)
    if (active) {
      setEraseMode(false)
      setSamMode(false)
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!submissionId) {
      setSubmitError("No active submission.")
      return
    }

    // Safety net: re-run the client precheck for every currently-completed
    // module. The `useModuleCompletionWatcher` hook normally keeps this in
    // sync as the user edits, but a rapid edit-then-submit might beat that
    // effect; guard here regardless.
    const stillInvalid: Array<{ name: string; error: string }> = []
    for (const module of interactiveModules) {
      const completed = moduleCompletions.find((c) => c.projectModuleId === module.id)?.completed
      if (!completed) continue
      const check = validateModuleCompletion({
        module,
        modules: interactiveModules,
        masks,
        scores
      })
      if (!check.valid && check.errors[0]) {
        stillInvalid.push({ name: module.template_name, error: check.errors[0] })
      }
    }
    if (stillInvalid.length > 0) {
      const summary = stillInvalid.map((e) => `${e.name}: ${e.error}`).join("\n")
      setSubmitError(`Cannot submit — some modules are incomplete:\n${summary}`)
      toast.error("Cannot submit case", { description: summary })
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await uploadHistoryNow("submit")
      await api(`/submissions/${submissionId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "submitted" })
      })
      await queryClient.invalidateQueries({
        queryKey: ["projects", projectId!, "cases"]
      })
      navigate(`/projects/${projectId}`)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit case")
    } finally {
      setIsSubmitting(false)
    }
  }, [
    api,
    interactiveModules,
    masks,
    moduleCompletions,
    navigate,
    projectId,
    queryClient,
    scores,
    submissionId,
    uploadHistoryNow
  ])

  useEffect(() => {
    if (!activeModuleId && interactiveModules[0]) {
      handleSelectModule(interactiveModules[0].id)
    }
  }, [activeModuleId, handleSelectModule, interactiveModules])

  useEffect(() => {
    if (!moduleCompletionError) return
    // Clear the error when the active module changes (user navigated away).
    if (moduleCompletionError.moduleId !== activeModule?.id) {
      setModuleCompletionError(null)
      return
    }
    // Re-run precheck: if it now passes, clear the error.
    if (activeModule) {
      const check = validateModuleCompletion({
        module: activeModule,
        modules: interactiveModules,
        masks,
        scores
      })
      if (check.valid) setModuleCompletionError(null)
    }
  }, [moduleCompletionError, activeModule, interactiveModules, masks, scores])

  useAnnotationHistoryBridge(!isBootstrappingSession && hasHistorySession)

  useModuleCompletionWatcher({
    modules: interactiveModules,
    moduleCompletions,
    masks,
    scores,
    imageLabels,
    texts,
    setModuleCompletionRemote,
    isReadOnly: isBootstrappingSession || isViewOnlyMode || (isReviewMode && !reviewEditEnabled)
  })

  if (!(caseId && projectId)) {
    return (
      <div className="h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">Invalid viewer route.</p>
      </div>
    )
  }

  return (
    <div className="h-screen bg-background text-foreground flex flex-col">
      <header className="h-10 border-b border-border flex items-center px-3 gap-2 bg-card shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          aria-label="Back to project"
          onClick={() => navigate(`/projects/${projectId}`)}
        >
          <ArrowLeft className="size-3.5" />
        </Button>

        <span className="text-[12px] font-medium truncate max-w-[160px]">{caseData?.study_identifier ?? caseId}</span>
        <Badge variant="outline" className="text-[9px] shrink-0 h-4 px-1">
          {isViewOnlyMode ? "View Only" : isReviewMode ? "Review" : "In Progress"}
        </Badge>
        {!isViewOnlyMode && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            {isHistoryDirty ? "Unsynced" : "Saved"} · {checkpointCount}cp
          </span>
        )}
        {submitError ? (
          <span className="text-[10px] text-destructive truncate max-w-[220px]">{submitError}</span>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          {!isViewOnlyMode && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                title="Undo (⌘Z)"
                onClick={() => applySnapshot(useHistoryStore.getState().undo())}
              >
                <Undo2 className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                title="Redo (⌘⇧Z)"
                onClick={() => applySnapshot(useHistoryStore.getState().redo())}
              >
                <Redo2 className="size-3.5" />
              </Button>
              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}
          {isViewOnlyMode ? (
            <Button size="sm" className="h-6 px-2 text-[11px]" onClick={() => void startAnnotating()}>
              <Pencil className="size-3 mr-1" />
              Start Annotating
            </Button>
          ) : isReviewMode ? (
            <ReviewActionBar
              submissionId={reviewSubmissionId!}
              status={reviewStatus ?? reviewSubmission?.status ?? "submitted"}
              reviewComment={reviewSubmission?.review_comment ?? null}
              editEnabled={reviewEditEnabled}
              onEditEnabledChange={setReviewEditEnabled}
              onStatusChange={(newStatus) => {
                setReviewStatus(newStatus)
                void refetchReviewSubmission()
              }}
              onSaveAndAccept={async () => {
                await uploadHistoryNow("save")
              }}
              disabled={isBootstrappingSession}
            />
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => void uploadHistoryNow("save")}
                disabled={isBootstrappingSession || isUploadingHistory || isSubmitting}
              >
                <Save className="size-3 mr-1" />
                Save
              </Button>
              <Button
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => void handleSubmit()}
                disabled={isBootstrappingSession || isUploadingHistory || isSubmitting}
              >
                <Send className="size-3 mr-1" />
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </>
          )}
        </div>
      </header>

      <ResizablePanelGroup orientation="horizontal" className="flex-1 min-h-0">
        <ResizablePanel
          panelRef={leftPanelRef}
          defaultSize={18}
          minSize={8}
          collapsible
          collapsedSize={2}
          onResize={(size) => setLeftCollapsed(size.asPercentage <= 2)}
          className="bg-card border-r border-border flex flex-col"
        >
          {leftCollapsed ? (
            <PullTab direction="right" onClick={() => leftPanelRef.current?.expand()} />
          ) : (
            <>
              <LeftSeriesPanel
                series={series as CaseSeries[]}
                activeSeriesId={activeSeriesId}
                projectName={projectData?.name}
                caseName={caseData?.name ?? caseData?.study_identifier}
                studyDate={studyDate}
                onSelectSeries={setSelectedSeriesId}
                onDragStart={(event, id) => {
                  setDraggedSeriesId(event.dataTransfer, id)
                  event.dataTransfer.effectAllowed = "copy"
                  setIsSeriesDragging(true)
                }}
                onDragEnd={() => setIsSeriesDragging(false)}
              />
              <ClinicalHistoryPanel clinicalHistory={caseData?.clinical_history} />
              {hasAnnotationTimeModule && <AnnotationTimeStatusBar caseId={caseId} />}
            </>
          )}
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={60} minSize={30} className="min-h-0 bg-surface-sunken">
          <div className="h-full p-1.5 flex min-h-0 flex-col gap-2">
            <div className="min-h-0 flex-1">
              <NvScenePanel
                caseId={caseId}
                projectId={projectId}
                series={series}
                isSeriesDragging={isSeriesDragging}
                masks={masks}
                maskDrawing={maskDrawingConfig ? { ...maskDrawingConfig, onBitmapCommit: handleBitmapCommit } : null}
                onActiveViewerSeriesChange={setActiveViewerSeriesId}
                snapshotToRestore={pendingRestoreSnapshot}
                onSnapshotRestored={clearPendingRestoreSnapshot}
                activeMaskLabelKey={activeMaskLabelKey}
                onSamStateChange={setSamState}
                getAuthHeaders={getAuthHeaders}
                maskOpacityById={maskOpacityById}
              />
            </div>
            {canViewTimeline && (
              <HistoryTimeline
                checkpointCount={historyCheckpointCount}
                cursor={historyCursor}
                disabled={isBootstrappingSession || !hasHistorySession}
                onScrub={handleTimelineScrub}
              />
            )}
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel
          panelRef={rightPanelRef}
          defaultSize={22}
          minSize={10}
          collapsible
          collapsedSize={2}
          onResize={(size) => setRightCollapsed(size.asPercentage <= 2)}
          className="bg-card border-l border-border flex flex-col"
        >
          {rightCollapsed ? (
            <PullTab direction="left" onClick={() => rightPanelRef.current?.expand()} />
          ) : (
            <RightModulePanel
              modules={interactiveModules}
              activeModule={activeModule}
              onSelectModule={handleSelectModule}
              moduleCompletions={moduleCompletions}
              moduleLockById={moduleLockById}
              isActiveModuleComplete={isActiveModuleComplete}
              isActiveModuleEditable={isActiveModuleEditable}
              activeModuleLockReason={activeModuleLockReason}
              onMarkComplete={() => void handleMarkActiveModuleComplete()}
              completionErrors={
                moduleCompletionError && moduleCompletionError.moduleId === activeModule?.id
                  ? moduleCompletionError.errors
                  : null
              }
              canComplete={
                !isReviewMode && Boolean(submissionId) && Boolean(activeModule?.id) && isActiveModuleEditable
              }
              activeVectorLabelKey={isActiveModuleEditable ? activeVectorLabelKey : null}
              onActiveVectorLabelKey={handleActiveVectorLabelKey}
              activeMaskInstanceId={activeMaskInstanceId}
              onSelectMaskInstance={setActiveMaskInstanceId}
              onActivateSingleMaskLabel={activateSingleMaskLabel}
              onCreateMaskInstance={createMaskInstance}
              onDeleteMaskInstance={deleteMaskInstance}
              drawingDisabled={drawingDisabled}
              onDrawingDisabled={setDrawingDisabled}
              activeGeometryType={activeGeometryType}
              onActiveGeometryType={handleActiveGeometryType}
              brushSize={brushSize}
              onBrushSize={setBrushSize}
              filledPen={filledPen}
              onFilledPen={setFilledPen}
              eraseMode={eraseMode}
              onEraseMode={setEraseMode}
              samMode={samMode}
              onSamMode={handleSamMode}
              samAvailable={samState.samAvailable}
              samPending={samState.samPending}
              samScribbleMode={samScribbleMode}
              onSamScribbleMode={handleSamScribbleMode}
              samConfidence={samConfidence}
              onSamConfidence={setSamConfidence}
              maskOpacityById={maskOpacityById}
              onMaskOpacityChange={handleMaskOpacityChange}
              onMaskOpacityCommit={handleMaskOpacityCommit}
              onMaskVisibilityToggle={handleMaskVisibilityToggle}
              masks={masks}
              scores={scores}
              imageLabels={imageLabels}
              texts={texts}
              onUpsertScore={handleUpsertScore}
              onUpsertImageLabel={handleUpsertImageLabel}
              onUpsertText={handleUpsertText}
              activeSeriesId={activeSeriesId ?? null}
              activeMaskSeriesId={activeMaskSeriesId}
              availableSeries={series as CaseSeries[]}
              maskFilterMismatchReason={maskFilterMismatchReason}
            />
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}
